/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { catchError, CodedError, err, ok, Result } from '@karya/error-values';

/**
 * Details for JWT parsing errors
 * @see {@link JwtParseError}
 */
type JwtParseErrorDetails = {
  reason: 'invalid_format' | 'token_expired';
  cause?: unknown;
};

/**
 * Error representing JWT parsing failures
 */
class JwtParseError extends CodedError<'JwtParseError', JwtParseErrorDetails> {
  constructor(reason: JwtParseErrorDetails['reason'], cause?: unknown) {
    const message = `Failed to parse JWT: ${reason}`;
    super('JwtParseError', message, { reason, cause });
  }
}

/**
 * Represents the payload of a Karya JWT token
 */
export interface KaryaJwtPayload {
  /** Expiration time in seconds */
  exp: number;
  /** Issued at time in seconds */
  iat: number;
  /** Entity type */
  entity: 'worker';
  /** Worker ID */
  sub: string;
  /** Registration status */
  is_registered: boolean;
}

/**
 * Parses and validates the Karya worker ID token from the active worker
 * @returns The parsed JWT payload or a {@link JwtParseError} on failure
 */
export function parseKaryaIdToken(idToken: string): Result<KaryaJwtPayload, JwtParseError> {
  const [tokenPayload, invalidTokenError] = catchError(
    () => {
      // Decode JWT and check expiration
      const jwtDecodedStr = atob(idToken.split('.')[1]);
      const jwtDecoded = JSON.parse(jwtDecodedStr) as KaryaJwtPayload;
      return jwtDecoded;
    },
    (e) => new JwtParseError('invalid_format', e),
  ).toTuple();

  if (invalidTokenError) {
    return err(invalidTokenError);
  }

  const currentTime = Math.floor(Date.now() / 1000);
  if (tokenPayload.exp < currentTime) {
    return err(new JwtParseError('token_expired'));
  }

  return ok(tokenPayload);
}
